import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Building2, UserCog, Users, 
  Globe, Lock, ShieldCheck, 
  Plus, Save, X, Check, Eye, SquarePen, Shield
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../components/ui/utils";
import { toast } from "sonner";
import api from "../../utils/api";
import MemberSelectModal from "../../components/service-desk/MemberSelectModal";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from "../../components/ui/dialog";

interface Member {
  id: string;
  name: string;
  email: string;
  initials: string;
  color: string;
  role?: string;
  permissions?: string[];
  userRole?: string;
  archived?: boolean;
}

export default function CreateServiceDesk() {
  const navigate = useNavigate();
  
  // Form State
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // const [themeColor, setThemeColor] = useState("#3b82f6");
  const [teamAdmin, setTeamAdmin] = useState<Member | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [authorizedRequesters, setAuthorizedRequesters] = useState<Member[]>([]);
  const [submissionControl, setSubmissionControl] = useState<'open' | 'restricted'>('open');

  // Modal State
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState<Member | null>(null);
  const [selectedRole, setSelectedRole] = useState<'Viewer' | 'Editor' | 'Team Admin'>('Viewer');

  const [isSelectAdminOpen, setIsSelectAdminOpen] = useState(false);
  const [isAddRequesterOpen, setIsAddRequesterOpen] = useState(false);

  const isFormValid = name.trim() !== "" && description.trim() !== "" && teamAdmin !== null;

  const handleAddMemberSelect = (user: any) => {
    if (members.some(m => String(m.id) === String(user.id))) {
      toast.error(`${user.name} is already added to the team.`);
      return;
    }
    
    setSelectedUserForRole(user);
    setSelectedRole('Viewer');
    setIsAddMemberOpen(false);
    setIsRoleModalOpen(true);
  };

  const handleConfirmAddMember = () => {
    if (!selectedUserForRole) return;

    const hasAgentPerm = selectedUserForRole?.permissions?.includes('manage:service_desk_agent');
    const hasViewerOnly = !hasAgentPerm && selectedUserForRole?.permissions?.includes('manage:service_desk_agent_viewer');
    const hasTeamAdminPerm = selectedUserForRole?.permissions?.includes('manage:service_desk_team_admin');

    if (hasViewerOnly && selectedRole !== 'Viewer') {
      toast.error(`${selectedUserForRole.name} is restricted to the Viewer role based on system permissions.`);
      return;
    }

    if (selectedRole === 'Team Admin' && !hasTeamAdminPerm) {
      toast.error(`${selectedUserForRole.name} does not have the required 'manage:service_desk_team_admin' permission to be a Team Administrator.`);
      return;
    }
    
    if (selectedRole === 'Team Admin') {
      // If there's an existing admin, move them to members as Editor
      if (teamAdmin) {
        setMembers(prev => [...prev, { ...teamAdmin, role: 'Editor' }]);
      }
      setTeamAdmin(selectedUserForRole);
      toast.success(`${selectedUserForRole.name} set as Team Administrator.`);
    } else {
      setMembers(prev => [...prev, { ...selectedUserForRole, role: selectedRole }]);
      toast.success(`${selectedUserForRole.name} added as ${selectedRole}.`);
    }

    setIsRoleModalOpen(false);
    setSelectedUserForRole(null);
    setSelectedRole('Viewer');
  };

  const handleRemoveMember = (id: string) => {
    setMembers(prev => prev.filter(m => String(m.id) !== String(id)));
  };

  const handleSelectAdmin = (user: any) => {
    const hasAgentPerm = user?.permissions?.includes('manage:service_desk_agent');
    const hasViewerOnly = !hasAgentPerm && user?.permissions?.includes('manage:service_desk_agent_viewer');
    const hasTeamAdminPerm = user?.permissions?.includes('manage:service_desk_team_admin');

    if (hasViewerOnly) {
      toast.error(`${user.name} is restricted to the Viewer role and cannot be a Team Administrator.`);
      return;
    }

    if (!hasTeamAdminPerm) {
      toast.error(`${user.name} does not have the required 'manage:service_desk_team_admin' permission to be a Team Administrator.`);
      return;
    }

    setTeamAdmin(user);
    // If this user was in the members list, remove them (since they are now Admin)
    setMembers(prev => prev.filter(m => String(m.id) !== String(user.id)));
    setIsSelectAdminOpen(false);
    toast.success(`${user.name} selected as Team Administrator.`);
  };

  const handleAddRequester = (user: any) => {
    if (authorizedRequesters.some(u => String(u.id) === String(user.id))) {
      toast.error(`${user.name} is already an authorized requester.`);
      return;
    }
    setAuthorizedRequesters(prev => [...prev, user]);
    setIsAddRequesterOpen(false);
    toast.success(`${user.name} can now submit requests.`);
  };

  const handleCreate = async () => {
    try {
      const response = await api.post('/api/service-desk', {
        name,
        description,
        // themeColor,
        teamAdminId: teamAdmin?.id,
        members: members.map(m => ({ userId: m.id, role: m.role?.toUpperCase() || 'VIEWER' })),
        submissionControl,
        authorizedRequesters: authorizedRequesters.map(u => u.id)
      });

      if (response.success) {
        toast.success(`Service Desk "${name}" created successfully!`);
        // Notify sidebar to refresh
        window.dispatchEvent(new CustomEvent('service-desks-changed'));
        navigate('/service-desk/settings');
      } else {
        toast.error(response.message || "Failed to create service desk.");
      }
    } catch (error) {
      console.error("Create desk error:", error);
      toast.error("An error occurred while creating the service desk.");
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] lg:h-screen overflow-hidden bg-background -m-4 md:-m-6 lg:-mt-6">
      {/* Header */}
      <div className="p-6 border-b border-border bg-white shrink-0">
        <div className="flex items-center gap-4 mb-2 w-full">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Create New Service Desk</h1>
              <p className="text-sm text-muted-foreground">Set up your service desk team and access controls</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 bg-white">
        <div className="max-w-3xl mx-auto space-y-8 pb-20">
          
          {/* Basic Information */}
          <div className="bg-white border border-border rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-bold mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium">Service Desk Name <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  placeholder="e.g., Facilities Service Desk" 
                  className="w-full px-4 py-2 bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium">Description <span className="text-red-500">*</span></label>
                <textarea 
                  placeholder="Brief description of what this service desk handles..." 
                  rows={3} 
                  className="w-full px-4 py-2 bg-input-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Team Administrator */}
          <div className="bg-white border border-border rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <UserCog className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-bold">Team Administrator <span className="text-red-500">*</span></h2>
            </div>
            
            {teamAdmin ? (
              <div className="flex items-center justify-between p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={cn("w-10 h-10 rounded-full flex items-center justify-center text-white font-bold", teamAdmin.color)}>
                    {teamAdmin.initials}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-bold text-gray-900">{teamAdmin.name}</div>
                    </div>
                    <div className="text-sm text-gray-500">{teamAdmin.email}</div>
                  </div>
                </div>
                <button onClick={() => setTeamAdmin(null)} className="p-2 hover:bg-red-50 text-red-500 rounded-lg transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setIsSelectAdminOpen(true)}
                className="w-full flex items-center justify-center gap-2 p-6 border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all group"
              >
                <UserCog className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                <span className="text-muted-foreground group-hover:text-primary font-medium transition-colors">Select Team Administrator</span>
              </button>
            )}
          </div>

          {/* Team Members */}
          <div className="bg-white border border-border rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold">Team Members</h2>
                <span className="text-sm text-muted-foreground font-medium">({members.length})</span>
              </div>
              <div className="flex items-center">
                <Button 
                  onClick={() => setIsAddMemberOpen(true)} 
                  className="hidden sm:flex items-center gap-2 h-9 font-bold"
                >
                  <Plus className="w-4 h-4" />
                  Add Member
                </Button>
                <Button 
                  onClick={() => setIsAddMemberOpen(true)} 
                  size="icon"
                  className="sm:hidden h-10 w-10 shadow-md"
                >
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            <div className="space-y-3">
              {members.length > 0 ? (
                members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:border-primary/30 transition-colors shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className={cn("w-8 h-8 text-xs rounded-full flex items-center justify-center text-white font-medium", member.color)}>
                        {member.initials}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-bold text-gray-900">{member.name}</div>
                        </div>
                        <div className="text-xs text-muted-foreground">{member.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className={cn(
                            "px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm border",
                            member.role === 'Editor' 
                                ? "bg-blue-50 text-blue-700 border-blue-100" 
                                : "bg-gray-100 text-gray-600 border-gray-200"
                        )}>
                            {member.role}
                        </div>
                        <button 
                          onClick={() => handleRemoveMember(member.id)}
                          className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-border rounded-xl bg-gray-50/50">
                  <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-gray-900 font-bold">No team members added yet</p>
                  <p className="text-xs text-muted-foreground mt-1 px-4">Add team members who will handle service requests</p>
                </div>
              )}
            </div>
          </div>

          {/* Request Submission Control */}
          <div className="bg-white border border-border rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-bold">Request Submission Control</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6 font-medium">Control who can submit new requests to this service desk</p>
            
            <div className="space-y-6">
              <div>
                <label className="block mb-3 text-sm font-medium text-gray-700">Access Level</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { id: 'open', label: 'Open to All', desc: 'Anyone can submit requests', icon: <Globe className="w-5 h-5" /> },
                    { id: 'restricted', label: 'Restricted', desc: 'Specific users only', icon: <Lock className="w-5 h-5" /> }
                  ].map((opt) => (
                    <button 
                      key={opt.id}
                      onClick={() => setSubmissionControl(opt.id as any)}
                      className={cn(
                        "flex items-center gap-3 p-4 border rounded-xl transition-all text-left",
                        submissionControl === opt.id 
                          ? "border-primary bg-primary/5 shadow-sm ring-1 ring-primary" 
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <div className={cn(
                        "p-2 rounded-lg",
                        submissionControl === opt.id ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                      )}>
                        {opt.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-sm text-gray-900 mb-0.5">{opt.label}</div>
                        <div className="text-[11px] text-muted-foreground leading-tight font-medium">{opt.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {submissionControl === 'restricted' ? (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">Authorized Users ({authorizedRequesters.length})</label>
                    <Button onClick={() => setIsAddRequesterOpen(true)} size="sm" className="gap-2">
                      <Plus className="w-3 h-3" /> Add User
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {authorizedRequesters.length > 0 ? (
                      authorizedRequesters.map((user, idx) => (
                        <div key={user.id} className="flex items-center justify-between p-3 border border-border rounded-lg bg-card shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className={cn("w-8 h-8 text-xs rounded-full flex items-center justify-center text-white font-medium", user.color)}>
                              {user.initials}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-bold text-gray-900">{user.name}</div>
                              </div>
                              <div className="text-xs text-muted-foreground">{user.email}</div>
                            </div>
                          </div>
                          <button 
                            onClick={() => setAuthorizedRequesters(authorizedRequesters.filter((_, i) => i !== idx))}
                            className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground text-sm bg-muted rounded-lg border border-dashed border-border/50">
                        <Lock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="font-bold text-gray-900">No authorized requesters yet</p>
                        <p className="text-xs mt-1">Add users who can submit requests to this service desk</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-4 rounded-xl text-sm bg-green-50/50 border border-green-200 text-green-700">
                  <Globe className="w-4 h-4 shrink-0" />
                  <span className="font-medium">
                    This service desk will be open to all employees in the organization
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-border bg-white shrink-0 pb-10 sm:pb-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 w-full">
          <Button 
            variant="outline" 
            onClick={() => navigate(-1)} 
            className="h-12 px-6 text-sm font-bold border-slate-200 text-slate-700 hover:bg-slate-50 transition-all shrink-0"
          >
            Cancel
          </Button>
          <Button 
            disabled={!isFormValid}
            onClick={handleCreate}
            className="h-12 flex-1 sm:flex-none sm:px-8 gap-2 text-sm font-bold shadow-md bg-slate-900 hover:bg-slate-800 text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" /> Create Service Desk
          </Button>
        </div>
      </div>

      {/* Modals */}
      <MemberSelectModal 
        isOpen={isAddMemberOpen}
        onClose={() => setIsAddMemberOpen(false)}
        onAdd={handleAddMemberSelect}
        deskTitle="New Service Desk"
        title="Add Team Member"
        stepDescription="Step 1: Select a user to join the support team"
        selectedIds={members.map(m => String(m.id))}
        excludeIds={teamAdmin?.id ? [String(teamAdmin.id)] : []}
      />

      <MemberSelectModal 
        isOpen={isSelectAdminOpen}
        onClose={() => setIsSelectAdminOpen(false)}
        onAdd={handleSelectAdmin}
        deskTitle="Selection for Admin"
        title="Select Team Administrator"
        stepDescription="Nominate a primary lead for this service desk"
        selectedIds={teamAdmin ? [String(teamAdmin.id)] : []}
        filterUser={(u) => u.permissions?.includes('manage:service_desk_team_admin') ?? false}
      />

      <MemberSelectModal 
        isOpen={isAddRequesterOpen}
        onClose={() => setIsAddRequesterOpen(false)}
        onAdd={handleAddRequester}
        deskTitle="Authorized Requester Selection"
        title="Authorize Requester"
        stepDescription="Select a user who can submit requests to this desk"
        selectedIds={authorizedRequesters.map(u => String(u.id))}
      />

      {/* Role Selection Modal (Step 2) */}
      {isRoleModalOpen && (
        <Dialog open={isRoleModalOpen} onOpenChange={setIsRoleModalOpen}>
          <DialogContent hideClose={true} className="p-0 sm:max-w-md border-none overflow-hidden bg-white shadow-xl">
            <DialogHeader className="p-6 border-b border-border flex-row items-center justify-between space-y-0 text-left">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <DialogTitle className="text-xl font-bold">Assign Role</DialogTitle>
              </div>
              <button 
                onClick={() => setIsRoleModalOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-900"
              >
                <X className="w-5 h-5" />
              </button>
            </DialogHeader>

            <div className="p-6">
              <DialogDescription className="text-sm text-muted-foreground mb-4">
                Step 2: Assign a role for {selectedUserForRole?.name}
              </DialogDescription>

              <div className="space-y-3">
                <button 
                  onClick={() => setSelectedRole('Viewer')}
                  className={cn(
                    "w-full flex items-center gap-4 p-4 border rounded-xl transition-all text-left",
                    selectedRole === 'Viewer' 
                      ? "bg-primary/5 border-primary shadow-sm ring-1 ring-primary" 
                      : "bg-card border-border hover:border-primary/50"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-lg",
                    selectedRole === 'Viewer' ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  )}>
                    <Eye className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold">Viewer</div>
                    <div className="text-sm text-muted-foreground">Can view requests and comments only.</div>
                  </div>
                  {selectedRole === 'Viewer' && <Check className="w-5 h-5 text-primary" />}
                </button>

                {(() => {
                  const hasAgentPerm = selectedUserForRole?.permissions?.includes('manage:service_desk_agent');
                  const hasViewerOnly = !hasAgentPerm && selectedUserForRole?.permissions?.includes('manage:service_desk_agent_viewer');
                  const hasTeamAdminPerm = selectedUserForRole?.permissions?.includes('manage:service_desk_team_admin');
                  const canBeTeamAdmin = hasTeamAdminPerm && !hasViewerOnly;
                  
                  return (
                    <div className="space-y-2">
                      <button 
                        onClick={() => !hasViewerOnly && setSelectedRole('Editor')}
                        disabled={hasViewerOnly}
                        className={cn(
                          "w-full flex items-center gap-4 p-4 border rounded-xl transition-all text-left",
                          selectedRole === 'Editor' 
                            ? "bg-blue-50 border-blue-200 shadow-sm ring-1 ring-blue-500" 
                            : "bg-card border-border hover:border-primary/50",
                          hasViewerOnly && "opacity-50 cursor-not-allowed grayscale-[0.5]"
                        )}
                      >
                        <div className={cn(
                          "p-2 rounded-lg",
                          selectedRole === 'Editor' ? "bg-blue-100 text-blue-600" : "bg-muted text-muted-foreground"
                        )}>
                          <SquarePen className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold">Editor</div>
                          <div className="text-sm text-muted-foreground">Can manage requests, add comments, and change status.</div>
                        </div>
                        {selectedRole === 'Editor' && <Check className="w-5 h-5 text-blue-600" />}
                      </button>

                      <button 
                        onClick={() => canBeTeamAdmin && setSelectedRole('Team Admin')}
                        disabled={!canBeTeamAdmin}
                        className={cn(
                          "w-full flex items-center gap-4 p-4 border rounded-xl transition-all text-left",
                          selectedRole === 'Team Admin' 
                            ? "bg-purple-50 border-purple-200 shadow-sm ring-1 ring-purple-500" 
                            : "bg-card border-border hover:border-primary/50",
                          !canBeTeamAdmin && "opacity-50 cursor-not-allowed grayscale-[0.5]"
                        )}
                      >
                        <div className={cn(
                          "p-2 rounded-lg",
                          selectedRole === 'Team Admin' ? "bg-purple-100 text-purple-600" : "bg-muted text-muted-foreground"
                        )}>
                          <Shield className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-bold">Team Admin</div>
                          <div className="text-sm text-muted-foreground">Full access to manage requests, forms, and team members.</div>
                        </div>
                        {selectedRole === 'Team Admin' && <Check className="w-5 h-5 text-purple-600" />}
                      </button>
                      {hasViewerOnly && (
                        <div className="px-2 flex items-center gap-2 text-[10px] text-amber-600 font-bold uppercase tracking-wider animate-in fade-in duration-300">
                          <Shield className="w-3 h-3" />
                          <span>Restricted to Viewer role based on system permissions</span>
                        </div>
                      )}

                      {!canBeTeamAdmin && !hasViewerOnly && (
                        <div className="px-2 flex items-center gap-2 text-[10px] text-gray-400 font-bold uppercase tracking-wider animate-in fade-in duration-300">
                          <Shield className="w-3 h-3" />
                          <span>The 'manage:service_desk_team_admin' permission is required</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>

            <DialogFooter className="p-6 border-t border-border flex items-center justify-end gap-3 bg-muted/30">
              <Button variant="ghost" onClick={() => { setIsRoleModalOpen(false); setIsAddMemberOpen(true); }}>
                Back
              </Button>
              <Button onClick={handleConfirmAddMember} className="px-6">
                Confirm & Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
